import React from 'react';
import _debounce from 'lodash/debounce';
import classNameBind from 'classnames/bind';
import { withTranslation } from '../Translator';
import Spinner from '../Spinner';
import Message from '../Message';
import Tooltip from '../Tooltip';
import Button from '../Button';
import { TextField } from '../TextField';
import uuid from '../../utils/methods/uuid';
import styles from './ListSidebar.module.css';
import ListSidebarOptions from './components/ListSidebarOptions';
import ListSidebarElement from './components/ListSidebarElement';
import ListSidebarBulkOperations from './components/ListSidebarBulkOperations';

const bindClasses = classNameBind.bind(styles);

class ListSidebar extends React.PureComponent {
  listRef = React.createRef()

  static defaultProps = {
    dataId: 'ListSidebar',
    listElementOnClick: () => {},
    buttonOnClick: null,
    listTopElement: null,
    buttonDisabled: false,
    searchValue: '',
    onSearchChange: null,
    onSearch: null,
    listSidebarOptions: null,
    bottomButtonText: '',
    isAbleToLoadMore: false,
    wait: 0,
    withFavorites: false,
    favoriteOnClick: () => {},
    bulkSelection: false,
    onCheckboxSelect: () => {},
    bulkSelectionOptions: [],
    bulkSelectionOnSelectAll: () => {},
  }

  constructor(props) {
    super(props);

    this.state = {
      searchValue: props.searchValue,
      debounce: null,
      bulkSelectionMode: false,
    };

    this.handleLoadMore = this.handleLoadMore.bind(this);
    this.scrollToElement = this.scrollToElement.bind(this);
  }

  static getDerivedStateFromProps(nextProps) {
    return {
      searchValue: nextProps.searchValue,
    };
  }

  handleSearchChange = (event) => {
    const { debounce } = this.state;
    const { onSearchChange, onSearch, wait } = this.props;

    onSearchChange(event);

    if (debounce && debounce.cancel) {
      debounce.cancel();
    }

    const triggerOnSearch = wait
      ? _debounce(() => onSearch(event), wait)
      : () => {};

    this.setState({
      debounce: triggerOnSearch,
    });

    triggerOnSearch();
  }

  handleLoadMore(e) {
    const {
      onLoadMoreClick,
    } = this.props;

    onLoadMoreClick(e, this.scrollToElement);
  }

  scrollToElement(id) {
    const {
      listData,
    } = this.props;
    const scrollToElementIndex = listData.map(item => item.id).indexOf(id);

    if (scrollToElementIndex !== -1) {
      const element = this.listRef.current.childNodes[scrollToElementIndex];

      if (element.scrollIntoView) {
        element.scrollIntoView();
      }
    }
  }

  renderMessage() {
    const {
      listData,
      error,
      isLoading,
      t,
    } = this.props;

    if (listData.length > 0) return null;

    return (
      <div className={bindClasses('message')}>
        {error && !isLoading ? <Message message={error} vertical={false} messageType="error" /> : null}
        {isLoading && <Spinner className="medium" />}
        {!error && !isLoading ? t('ListSidebar.noItem') : null}
      </div>
    );
  }

  renderLoadMore() {
    const {
      isAbleToLoadMore,
      isLoading,
      listData,
      t,
    } = this.props;

    if (!isAbleToLoadMore || listData.length === 0) return null;

    return (
      <div className={bindClasses('load-more')}>
        {isLoading
          ? <Spinner className="small" />
          : (
            <a
              onClick={this.handleLoadMore}
              tabIndex={0}
              role="button"
              data-ppuc-id="ListSidebar.loadMoreLink"
            >
              {t('ListSidebar.loadMore')}
            </a>
          )
        }
      </div>
    );
  }

  render() {
    const {
      searchValue,
      bulkSelectionMode,
    } = this.state;
    const {
      listData,
      listElementOnClick,
      buttonOnClick,
      buttonDisabled,
      buttonTooltip,
      searchPlaceholder,
      isLoading,
      listSidebarOptions,
      bottomButtonText,
      t,
      dataId,
      onSearch,
      listTopElement,
      onSearchChange,
      withFavorites,
      favoriteOnClick,
      bulkSelection,
      onCheckboxSelect,
      bulkSelectionOptions,
      bulkSelectionOnSelectAll,
      bulkSelectionNumberOfSelecetedElements,
    } = this.props;

    const searchEnabled = onSearch || onSearchChange;

    return (
      <div
        className={bindClasses('container')}
        data-ppuc-id={dataId}
      >
        {searchEnabled && (
          <TextField
            name="search"
            type="text"
            icon="search"
            value={searchValue}
            placeholder={searchPlaceholder || t('ListSidebar.searchPlaceholder')}
            onChange={this.handleSearchChange}
            onFocus={() => {}}
            clearable
          />
        )}
        <div
          className={bindClasses('list', { loading: isLoading, empty: listData.length === 0, 'with-search': searchEnabled })}
          data-ppuc-id="ListSidebar.list"
        >
          {(listSidebarOptions || bulkSelection)
            && (
              <ListSidebarOptions
                {...listSidebarOptions}
                bulkSelection={bulkSelection}
                bulkSelectionMode={bulkSelectionMode}
                onBulkSelectionModeChange={() => (this.setState({ bulkSelectionMode: !bulkSelectionMode }))}
              />
            )
          }
          {bulkSelection && bulkSelectionMode
            && (
              <ListSidebarBulkOperations
                bulkSelectionOptions={bulkSelectionOptions}
                bulkSelectionOnSelectAll={bulkSelectionOnSelectAll}
                listData={listData}
                bulkSelectionNumberOfSelecetedElements={bulkSelectionNumberOfSelecetedElements}
              />
            )
          }
          {listTopElement && listTopElement()}
          {this.renderMessage()}
          <div className={bindClasses('list-only')} ref={this.listRef}>
            {listData.map(listElement => (
              <ListSidebarElement
                data={listElement}
                onClick={listElementOnClick}
                key={listElement.id || uuid()}
                withFavorites={withFavorites}
                favoriteOnClick={favoriteOnClick}
                bulkSelectionMode={bulkSelectionMode}
                onCheckboxSelect={onCheckboxSelect}
              />
            ))}
            {this.renderLoadMore()}
          </div>
          {buttonOnClick && (
            <div
              className={bindClasses('list-bottom')}
              data-ppuc-id="ListSidebar.bottom"
            >
              <Tooltip content={buttonTooltip}>
                <Button
                  onClick={buttonOnClick}
                  text={bottomButtonText || t('addNewItem')}
                  disabled={buttonDisabled}
                  appearance="constructive"
                  fullWidth
                />
              </Tooltip>
            </div>
          )}
        </div>
      </div>
    );
  }
}

const enhanced = withTranslation('components')(ListSidebar);
enhanced.displayName = 'ListSidebar';

export default enhanced;
export const PureComponent = ListSidebar;
